import { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  GitCompare, CheckCircle2, XCircle, AlertTriangle, Clock, Search,
  Download, Eye, ThumbsUp, Flag, FileText, ArrowUpRight, ArrowDownLeft,
  Filter, RefreshCw, Loader2, IndianRupee, Shield,
} from "lucide-react";

// ========================
// TYPES
// ========================

type MatchStatus = "Matched" | "Mismatch" | "Missing in Books" | "Missing in GSTR-2B" | "Excess Claim";
type ReviewStatus = "Pending" | "Approved" | "Flagged" | "Resolved";

interface ReconciliationEntry {
  id: string;
  supplierName: string;
  supplierGstin: string;
  invoiceNo: string;
  invoiceDate: string;
  // Books data
  booksValue: number | null;
  booksCgst: number | null;
  booksSgst: number | null;
  booksIgst: number | null;
  booksTotalTax: number | null;
  // GSTR-2B data
  gstr2bValue: number | null;
  gstr2bCgst: number | null;
  gstr2bSgst: number | null;
  gstr2bIgst: number | null;
  gstr2bTotalTax: number | null;
  // Status
  matchStatus: MatchStatus;
  valueDiff: number;
  taxDiff: number;
  reviewStatus: ReviewStatus;
  reviewedBy: string | null;
  reviewNote: string | null;
  period: string;
}

// ========================
// MOCK DATA
// ========================

const MOCK_ENTRIES: ReconciliationEntry[] = [];

// ========================
// HELPERS
// ========================

const INR = (n: number | null) => n != null ? `₹${n.toLocaleString("en-IN")}` : "—";

const MatchBadge = ({ status }: { status: MatchStatus }) => {
  const map: Record<MatchStatus, { variant: "default" | "destructive" | "secondary" | "outline"; icon: typeof CheckCircle2 }> = {
    "Matched": { variant: "default", icon: CheckCircle2 },
    "Mismatch": { variant: "destructive", icon: AlertTriangle },
    "Missing in Books": { variant: "secondary", icon: ArrowDownLeft },
    "Missing in GSTR-2B": { variant: "secondary", icon: ArrowUpRight },
    "Excess Claim": { variant: "destructive", icon: XCircle },
  };
  const m = map[status];
  return <Badge variant={m.variant} className="gap-1 text-xs"><m.icon className="h-3 w-3" />{status}</Badge>;
};

const ReviewBadge = ({ status }: { status: ReviewStatus }) => {
  const map: Record<ReviewStatus, "default" | "destructive" | "secondary" | "outline"> = {
    Approved: "default", Flagged: "destructive", Pending: "secondary", Resolved: "outline",
  };
  return <Badge variant={map[status]}>{status}</Badge>;
};

// ========================
// MAIN PAGE
// ========================

const GSTReconciliationPage = () => {
  const { toast } = useToast();
  const { role } = useAuth();
  const isCA = role === "ca" || role === "company_admin";

  const [tab, setTab] = useState("overview");
  const [search, setSearch] = useState("");
  const [matchFilter, setMatchFilter] = useState<MatchStatus | "All">("All");
  const [reviewFilter, setReviewFilter] = useState<ReviewStatus | "All">("All");
  const [periodFilter, setPeriodFilter] = useState<string>("All");
  const [entries, setEntries] = useState(MOCK_ENTRIES);
  const [selectedEntry, setSelectedEntry] = useState<ReconciliationEntry | null>(null);
  const [reviewNote, setReviewNote] = useState("");
  const [syncing, setSyncing] = useState(false);

  const periods = useMemo(() => [...new Set(entries.map((e) => e.period))], [entries]);

  const filtered = useMemo(() => entries.filter((e) =>
    (matchFilter === "All" || e.matchStatus === matchFilter) &&
    (reviewFilter === "All" || e.reviewStatus === reviewFilter) &&
    (periodFilter === "All" || e.period === periodFilter) &&
    (e.supplierName.toLowerCase().includes(search.toLowerCase()) ||
     e.invoiceNo.toLowerCase().includes(search.toLowerCase()) ||
     e.supplierGstin.toLowerCase().includes(search.toLowerCase()))
  ), [entries, matchFilter, reviewFilter, periodFilter, search]);

  const stats = useMemo(() => ({
    total: entries.length,
    matched: entries.filter((e) => e.matchStatus === "Matched").length,
    mismatched: entries.filter((e) => e.matchStatus === "Mismatch").length,
    missingBooks: entries.filter((e) => e.matchStatus === "Missing in Books").length,
    missing2b: entries.filter((e) => e.matchStatus === "Missing in GSTR-2B").length,
    pending: entries.filter((e) => e.reviewStatus === "Pending").length,
    flagged: entries.filter((e) => e.reviewStatus === "Flagged").length,
    totalBooksITC: entries.reduce((s, e) => s + (e.booksTotalTax || 0), 0),
    total2bITC: entries.reduce((s, e) => s + (e.gstr2bTotalTax || 0), 0),
    matchRate: entries.length > 0 ? Math.round((entries.filter((e) => e.matchStatus === "Matched").length / entries.length) * 100) : 0,
  }), [entries]);

  const handleSync = () => {
    setSyncing(true);
    setTimeout(() => { setSyncing(false); toast({ title: "✅ Reconciliation Updated", description: "GSTR-2B data synced and compared with purchase books." }); }, 2000);
  };

  const handleApprove = (id: string) => {
    setEntries((prev) => prev.map((e) => e.id === id ? { ...e, reviewStatus: "Approved" as ReviewStatus, reviewedBy: "Current User (CA)", reviewNote: reviewNote || "Approved by CA" } : e));
    toast({ title: "Approved", description: "Entry marked as reviewed and approved." });
    setReviewNote("");
  };

  const handleFlag = (id: string) => {
    if (!reviewNote.trim()) { toast({ title: "Note Required", description: "Please add a note explaining the issue.", variant: "destructive" }); return; }
    setEntries((prev) => prev.map((e) => e.id === id ? { ...e, reviewStatus: "Flagged" as ReviewStatus, reviewedBy: "Current User (CA)", reviewNote } : e));
    toast({ title: "Flagged", description: "Entry flagged for follow-up." });
    setReviewNote("");
  };

  const handleResolve = (id: string) => {
    setEntries((prev) => prev.map((e) => e.id === id ? { ...e, reviewStatus: "Resolved" as ReviewStatus, reviewNote: reviewNote || e.reviewNote } : e));
    toast({ title: "Resolved", description: "Issue marked as resolved." });
    setReviewNote("");
  };

  const handleExportReport = () => {
    const rows = [
      ["Supplier", "GSTIN", "Invoice", "Date", "Books Value", "Books Tax", "GSTR-2B Value", "GSTR-2B Tax", "Value Diff", "Tax Diff", "Match", "Review", "CA Note"].join(","),
      ...entries.map((e) => [
        e.supplierName, e.supplierGstin, e.invoiceNo, e.invoiceDate,
        e.booksValue ?? "", e.booksTotalTax ?? "", e.gstr2bValue ?? "", e.gstr2bTotalTax ?? "",
        e.valueDiff, e.taxDiff, e.matchStatus, e.reviewStatus, `"${e.reviewNote || ""}"`,
      ].join(",")),
    ].join("\n");
    const blob = new Blob([rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "gst-reconciliation-report.csv"; a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Exported", description: "Reconciliation report downloaded as CSV." });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <GitCompare className="h-6 w-6 text-primary" /> GST Reconciliation
          </h1>
          <p className="text-muted-foreground mt-1">Compare purchase books with GSTR-2B and resolve discrepancies</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing}>
            {syncing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            {syncing ? "Syncing…" : "Sync GSTR-2B"}
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportReport}>
            <Download className="h-4 w-4 mr-1" /> Export Report
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: "Total Entries", value: stats.total, icon: FileText, color: "text-foreground" },
          { label: "Matched", value: `${stats.matched} (${stats.matchRate}%)`, icon: CheckCircle2, color: "text-green-600" },
          { label: "Mismatches", value: stats.mismatched, icon: AlertTriangle, color: "text-destructive" },
          { label: "Missing in Books", value: stats.missingBooks, icon: ArrowDownLeft, color: "text-yellow-600" },
          { label: "Pending Review", value: stats.pending, icon: Clock, color: "text-primary" },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <s.icon className={`h-4 w-4 ${s.color}`} />
                <span className="text-xs text-muted-foreground">{s.label}</span>
              </div>
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ITC Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">ITC as per Books</p>
            <p className="text-2xl font-bold text-foreground">{INR(stats.totalBooksITC)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">ITC as per GSTR-2B</p>
            <p className="text-2xl font-bold text-foreground">{INR(stats.total2bITC)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">ITC Difference</p>
            <p className={`text-2xl font-bold ${Math.abs(stats.totalBooksITC - stats.total2bITC) < 1 ? "text-green-600" : "text-destructive"}`}>
              {INR(Math.abs(stats.totalBooksITC - stats.total2bITC))}
              {stats.totalBooksITC > stats.total2bITC ? " (Books > 2B)" : stats.totalBooksITC < stats.total2bITC ? " (2B > Books)" : ""}
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="overview">All Entries</TabsTrigger>
          <TabsTrigger value="mismatches">Mismatches ({stats.mismatched})</TabsTrigger>
          <TabsTrigger value="missing">Missing ({stats.missingBooks + stats.missing2b})</TabsTrigger>
          <TabsTrigger value="review">CA Review ({stats.pending + stats.flagged})</TabsTrigger>
        </TabsList>

        {/* =================== TAB: ALL ENTRIES =================== */}
        <TabsContent value="overview" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 bg-card border border-border rounded-md px-3 py-2 flex-1 max-w-sm">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search supplier, invoice, GSTIN..."
                className="bg-transparent text-sm outline-none w-full text-foreground placeholder:text-muted-foreground" />
            </div>
            <Select value={matchFilter} onValueChange={(v) => setMatchFilter(v as MatchStatus | "All")}>
              <SelectTrigger className="w-[180px] h-9 text-sm"><SelectValue placeholder="Match Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Statuses</SelectItem>
                <SelectItem value="Matched">Matched</SelectItem>
                <SelectItem value="Mismatch">Mismatch</SelectItem>
                <SelectItem value="Missing in Books">Missing in Books</SelectItem>
                <SelectItem value="Missing in GSTR-2B">Missing in GSTR-2B</SelectItem>
              </SelectContent>
            </Select>
            <Select value={periodFilter} onValueChange={setPeriodFilter}>
              <SelectTrigger className="w-[140px] h-9 text-sm"><SelectValue placeholder="Period" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Periods</SelectItem>
                {periods.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground ml-auto">{filtered.length} entries</span>
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Invoice</TableHead>
                      <TableHead className="text-right">Books Tax</TableHead>
                      <TableHead className="text-right">GSTR-2B Tax</TableHead>
                      <TableHead className="text-right">Diff</TableHead>
                      <TableHead>Match</TableHead>
                      <TableHead>Review</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((e) => (
                      <TableRow key={e.id} className={e.matchStatus !== "Matched" ? "bg-destructive/5" : ""}>
                        <TableCell>
                          <div>
                            <p className="text-sm font-medium text-foreground">{e.supplierName}</p>
                            <p className="text-xs text-muted-foreground font-mono">{e.supplierGstin}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-foreground">{e.invoiceNo}</p>
                          <p className="text-xs text-muted-foreground">{e.invoiceDate} • {e.period}</p>
                        </TableCell>
                        <TableCell className="text-right text-sm text-foreground">{INR(e.booksTotalTax)}</TableCell>
                        <TableCell className="text-right text-sm text-foreground">{INR(e.gstr2bTotalTax)}</TableCell>
                        <TableCell className="text-right">
                          {e.taxDiff !== 0 ? (
                            <span className={`text-sm font-medium ${e.taxDiff > 0 ? "text-destructive" : "text-yellow-600"}`}>
                              {e.taxDiff > 0 ? "+" : ""}{INR(e.taxDiff)}
                            </span>
                          ) : <span className="text-sm text-green-600">✓ 0</span>}
                        </TableCell>
                        <TableCell><MatchBadge status={e.matchStatus} /></TableCell>
                        <TableCell><ReviewBadge status={e.reviewStatus} /></TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                            onClick={() => { setSelectedEntry(e); setTab("review"); setReviewNote(""); }}>
                            <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* =================== TAB: MISMATCHES =================== */}
        <TabsContent value="mismatches" className="space-y-4">
          <Card className="border-destructive/20 bg-destructive/5">
            <CardContent className="p-4">
              <div className="flex gap-3">
                <AlertTriangle className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">ITC Mismatches Detected</p>
                  <p className="text-xs text-muted-foreground">These entries have different tax values between your books and GSTR-2B. Review and correct before filing.</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {entries.filter((e) => e.matchStatus === "Mismatch").map((e) => (
            <Card key={e.id}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">{e.invoiceNo} — {e.supplierName}</CardTitle>
                    <CardDescription>{e.supplierGstin} • {e.invoiceDate}</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <MatchBadge status={e.matchStatus} />
                    <ReviewBadge status={e.reviewStatus} />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">As per Books</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-muted-foreground">Taxable Value:</span></div><div className="text-right font-medium text-foreground">{INR(e.booksValue)}</div>
                      <div><span className="text-muted-foreground">CGST:</span></div><div className="text-right text-foreground">{INR(e.booksCgst)}</div>
                      <div><span className="text-muted-foreground">SGST:</span></div><div className="text-right text-foreground">{INR(e.booksSgst)}</div>
                      <div><span className="text-muted-foreground">IGST:</span></div><div className="text-right text-foreground">{INR(e.booksIgst)}</div>
                      <Separator className="col-span-2" />
                      <div><span className="font-medium text-foreground">Total Tax:</span></div><div className="text-right font-bold text-foreground">{INR(e.booksTotalTax)}</div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">As per GSTR-2B</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><span className="text-muted-foreground">Taxable Value:</span></div><div className="text-right font-medium text-foreground">{INR(e.gstr2bValue)}</div>
                      <div><span className="text-muted-foreground">CGST:</span></div><div className="text-right text-foreground">{INR(e.gstr2bCgst)}</div>
                      <div><span className="text-muted-foreground">SGST:</span></div><div className="text-right text-foreground">{INR(e.gstr2bSgst)}</div>
                      <div><span className="text-muted-foreground">IGST:</span></div><div className="text-right text-foreground">{INR(e.gstr2bIgst)}</div>
                      <Separator className="col-span-2" />
                      <div><span className="font-medium text-foreground">Total Tax:</span></div><div className="text-right font-bold text-foreground">{INR(e.gstr2bTotalTax)}</div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                  <p className="text-sm font-medium text-destructive">
                    Tax Difference: {INR(Math.abs(e.taxDiff))}
                    {e.valueDiff !== 0 && <> | Value Difference: {INR(Math.abs(e.valueDiff))}</>}
                  </p>
                </div>
                {e.reviewNote && (
                  <div className="mt-3 p-3 rounded-lg bg-muted/50 border border-border">
                    <p className="text-xs text-muted-foreground">CA Note: <span className="text-foreground">{e.reviewNote}</span></p>
                    <p className="text-xs text-muted-foreground mt-1">— {e.reviewedBy}</p>
                  </div>
                )}
                {isCA && e.reviewStatus === "Pending" && (
                  <div className="mt-4 space-y-3">
                    <Textarea placeholder="Add review note..." value={reviewNote} onChange={(ev) => setReviewNote(ev.target.value)} rows={2} />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleApprove(e.id)}><ThumbsUp className="h-4 w-4 mr-1" /> Approve</Button>
                      <Button size="sm" variant="destructive" onClick={() => handleFlag(e.id)}><Flag className="h-4 w-4 mr-1" /> Flag Issue</Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* =================== TAB: MISSING =================== */}
        <TabsContent value="missing" className="space-y-4">
          {/* Missing in Books */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <ArrowDownLeft className="h-4 w-4 text-yellow-600" /> Missing in Books
              </CardTitle>
              <CardDescription>Invoices present in GSTR-2B but not in your purchase books</CardDescription>
            </CardHeader>
            <CardContent>
              {entries.filter((e) => e.matchStatus === "Missing in Books").length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No missing entries</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Invoice</TableHead>
                      <TableHead className="text-right">GSTR-2B Value</TableHead>
                      <TableHead className="text-right">GSTR-2B Tax</TableHead>
                      <TableHead>Review</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.filter((e) => e.matchStatus === "Missing in Books").map((e) => (
                      <TableRow key={e.id}>
                        <TableCell>
                          <p className="text-sm font-medium text-foreground">{e.supplierName}</p>
                          <p className="text-xs text-muted-foreground font-mono">{e.supplierGstin}</p>
                        </TableCell>
                        <TableCell className="text-sm text-foreground">{e.invoiceNo}</TableCell>
                        <TableCell className="text-right text-sm text-foreground">{INR(e.gstr2bValue)}</TableCell>
                        <TableCell className="text-right text-sm text-foreground">{INR(e.gstr2bTotalTax)}</TableCell>
                        <TableCell><ReviewBadge status={e.reviewStatus} /></TableCell>
                        <TableCell className="text-right">
                          {isCA && e.reviewStatus === "Pending" && (
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleApprove(e.id)}>
                                <ThumbsUp className="h-3 w-3 mr-1" /> Accept
                              </Button>
                              <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => { setSelectedEntry(e); setTab("review"); }}>
                                <Flag className="h-3 w-3 mr-1" /> Flag
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Missing in GSTR-2B */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <ArrowUpRight className="h-4 w-4 text-destructive" /> Missing in GSTR-2B
              </CardTitle>
              <CardDescription>Invoices in your books but not reflected in supplier's GSTR-1 / your GSTR-2B</CardDescription>
            </CardHeader>
            <CardContent>
              {entries.filter((e) => e.matchStatus === "Missing in GSTR-2B").length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No missing entries</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Invoice</TableHead>
                      <TableHead className="text-right">Books Value</TableHead>
                      <TableHead className="text-right">Books Tax</TableHead>
                      <TableHead>Review</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entries.filter((e) => e.matchStatus === "Missing in GSTR-2B").map((e) => (
                      <TableRow key={e.id}>
                        <TableCell>
                          <p className="text-sm font-medium text-foreground">{e.supplierName}</p>
                          <p className="text-xs text-muted-foreground font-mono">{e.supplierGstin}</p>
                        </TableCell>
                        <TableCell className="text-sm text-foreground">{e.invoiceNo}</TableCell>
                        <TableCell className="text-right text-sm text-foreground">{INR(e.booksValue)}</TableCell>
                        <TableCell className="text-right text-sm text-foreground">{INR(e.booksTotalTax)}</TableCell>
                        <TableCell><ReviewBadge status={e.reviewStatus} /></TableCell>
                        <TableCell className="text-right">
                          {isCA && (
                            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setSelectedEntry(e); setTab("review"); }}>
                              <Eye className="h-3 w-3 mr-1" /> Review
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* =================== TAB: CA REVIEW =================== */}
        <TabsContent value="review" className="space-y-6">
          {selectedEntry ? (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{selectedEntry.invoiceNo} — {selectedEntry.supplierName}</CardTitle>
                    <CardDescription>{selectedEntry.supplierGstin} • {selectedEntry.invoiceDate} • {selectedEntry.period}</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <MatchBadge status={selectedEntry.matchStatus} />
                    <ReviewBadge status={selectedEntry.reviewStatus} />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Side-by-side comparison */}
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-3 p-4 rounded-lg border border-border bg-muted/30">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1"><FileText className="h-3 w-3" /> Purchase Books</p>
                    {selectedEntry.booksValue != null ? (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">Taxable Value</span><span className="font-medium text-foreground">{INR(selectedEntry.booksValue)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">CGST</span><span className="text-foreground">{INR(selectedEntry.booksCgst)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">SGST</span><span className="text-foreground">{INR(selectedEntry.booksSgst)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">IGST</span><span className="text-foreground">{INR(selectedEntry.booksIgst)}</span></div>
                        <Separator />
                        <div className="flex justify-between font-bold"><span className="text-foreground">Total Tax</span><span className="text-foreground">{INR(selectedEntry.booksTotalTax)}</span></div>
                      </div>
                    ) : (
                      <div className="py-6 text-center text-muted-foreground text-sm">
                        <XCircle className="h-6 w-6 mx-auto mb-2 opacity-30" />
                        Not found in books
                      </div>
                    )}
                  </div>
                  <div className="space-y-3 p-4 rounded-lg border border-border bg-muted/30">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Shield className="h-3 w-3" /> GSTR-2B</p>
                    {selectedEntry.gstr2bValue != null ? (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-muted-foreground">Taxable Value</span><span className="font-medium text-foreground">{INR(selectedEntry.gstr2bValue)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">CGST</span><span className="text-foreground">{INR(selectedEntry.gstr2bCgst)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">SGST</span><span className="text-foreground">{INR(selectedEntry.gstr2bSgst)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">IGST</span><span className="text-foreground">{INR(selectedEntry.gstr2bIgst)}</span></div>
                        <Separator />
                        <div className="flex justify-between font-bold"><span className="text-foreground">Total Tax</span><span className="text-foreground">{INR(selectedEntry.gstr2bTotalTax)}</span></div>
                      </div>
                    ) : (
                      <div className="py-6 text-center text-muted-foreground text-sm">
                        <XCircle className="h-6 w-6 mx-auto mb-2 opacity-30" />
                        Not found in GSTR-2B
                      </div>
                    )}
                  </div>
                </div>

                {/* Difference summary */}
                {(selectedEntry.valueDiff !== 0 || selectedEntry.taxDiff !== 0) && (
                  <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20">
                    <p className="text-sm font-medium text-destructive flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      {selectedEntry.taxDiff !== 0 && <>Tax Difference: {INR(Math.abs(selectedEntry.taxDiff))}</>}
                      {selectedEntry.valueDiff !== 0 && selectedEntry.taxDiff !== 0 && " | "}
                      {selectedEntry.valueDiff !== 0 && <>Value Difference: {INR(Math.abs(selectedEntry.valueDiff))}</>}
                    </p>
                  </div>
                )}

                {/* Existing review note */}
                {selectedEntry.reviewNote && (
                  <div className="p-3 rounded-lg bg-muted/50 border border-border">
                    <p className="text-xs text-muted-foreground mb-1">Previous Review Note:</p>
                    <p className="text-sm text-foreground">{selectedEntry.reviewNote}</p>
                    <p className="text-xs text-muted-foreground mt-1">— {selectedEntry.reviewedBy}</p>
                  </div>
                )}

                {/* CA Actions */}
                {isCA && (
                  <div className="space-y-3 pt-2">
                    <Textarea
                      placeholder="Add review note (required for flagging)..."
                      value={reviewNote}
                      onChange={(e) => setReviewNote(e.target.value)}
                      rows={3}
                    />
                    <div className="flex gap-2">
                      {selectedEntry.reviewStatus !== "Approved" && (
                        <Button onClick={() => handleApprove(selectedEntry.id)}>
                          <ThumbsUp className="h-4 w-4 mr-1" /> Approve
                        </Button>
                      )}
                      {selectedEntry.reviewStatus !== "Flagged" && (
                        <Button variant="destructive" onClick={() => handleFlag(selectedEntry.id)}>
                          <Flag className="h-4 w-4 mr-1" /> Flag Issue
                        </Button>
                      )}
                      {(selectedEntry.reviewStatus === "Flagged") && (
                        <Button variant="outline" onClick={() => handleResolve(selectedEntry.id)}>
                          <CheckCircle2 className="h-4 w-4 mr-1" /> Mark Resolved
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {!isCA && (
                  <div className="p-4 rounded-lg bg-muted/30 border border-border text-center">
                    <p className="text-sm text-muted-foreground">Only Chartered Accountants or Company Admins can approve or flag entries.</p>
                  </div>
                )}

                <Button variant="ghost" size="sm" onClick={() => { setSelectedEntry(null); setTab("overview"); }}>
                  ← Back to all entries
                </Button>
              </CardContent>
            </Card>
          ) : (
            /* Pending review list */
            <div className="space-y-4">
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="p-4">
                  <div className="flex gap-3">
                    <Shield className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">CA Review Queue</p>
                      <p className="text-xs text-muted-foreground">{stats.pending} entries pending review, {stats.flagged} flagged for follow-up</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {entries.filter((e) => e.reviewStatus === "Pending" || e.reviewStatus === "Flagged").map((e) => (
                <Card key={e.id} className={e.reviewStatus === "Flagged" ? "border-destructive/20" : ""}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="text-sm font-medium text-foreground">{e.invoiceNo} — {e.supplierName}</p>
                        <p className="text-xs text-muted-foreground">{e.supplierGstin} • {e.period}</p>
                      </div>
                      <MatchBadge status={e.matchStatus} />
                      <ReviewBadge status={e.reviewStatus} />
                      {e.taxDiff !== 0 && <span className="text-xs text-destructive font-medium">Δ {INR(Math.abs(e.taxDiff))}</span>}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => { setSelectedEntry(e); setReviewNote(""); }}>
                      <Eye className="h-4 w-4 mr-1" /> Review
                    </Button>
                  </CardContent>
                </Card>
              ))}

              {entries.filter((e) => e.reviewStatus === "Pending" || e.reviewStatus === "Flagged").length === 0 && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <CheckCircle2 className="h-10 w-10 mx-auto mb-3 text-green-500" />
                    <p className="font-medium text-foreground">All entries reviewed</p>
                    <p className="text-xs text-muted-foreground mt-1">No pending items in the review queue</p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GSTReconciliationPage;
